/* eslint-disable @next/next/no-img-element */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import Styles from './CustomEmblaCarouselHome24.css';
import PaginationBullets from '../PaginationBullets/PaginationBullets';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

function CustomEmblaCarouselHome24({
  children,
  fillHeight,
  loop,
  autoplay,
  onEmblaApiReady,
  onSlideChanged,
  hideOverflow,
  useBigArrows,
  enableBullets,
  options,
  enableMobileArrows,
  bulletCount,
  detectBulletCount,
  enablePrevArrow,
  ariaLabelBullet,
  wheelGestures,
  renderBullets,
  onChange,
  customEmblaCarouselRef,
  onPointer,
  ...props
}) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [enablePrev, setEnablePrev] = useState(enablePrevArrow ? false : true);
  const [enableNext, setEnableNext] = useState(true);
  const [snap, setSnap] = useState(0);

  const fillHeightClass = useMemo(() => (fillHeight ? 'fill-height' : ''), [fillHeight]);
  const overflowClass = useMemo(() => (hideOverflow ? 'hide-overflow' : ''), [hideOverflow]);
  const enablePrevClass = useMemo(() => (enablePrev ? '' : 'disabled'), [enablePrev]);
  const enableNextClass = useMemo(() => (enableNext ? '' : 'disabled'), [enableNext]);
  const bigArrowsClass = useMemo(() => (useBigArrows ? 'big-arrow' : ''), [useBigArrows]);
  const mobileArrowsClass = useMemo(
    () => (enableMobileArrows ? 'enable-mobile-arrows' : ''),
    [enableMobileArrows],
  );

  const autoplayRef = useRef(
    Autoplay({ delay: 5000, stopOnInteraction: false }, (emblaRoot) => emblaRoot.parentElement),
  );

  const wheelRef = useRef(WheelGesturesPlugin());

  const plugins = useMemo(() => {
    const p = [];

    if (autoplay) {
      p.push(autoplayRef.current);
    }

    if (wheelGestures) {
      p.push(wheelRef.current);
    }

    return p;
  }, [autoplay, wheelGestures]);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop,
      direction: 'lrf',
      align: 0,
      containScroll: 'trimSnaps',
      speed: 15,
      skipSnaps: wheelGestures || options.skipSnaps || undefined,
      ...options,
    },
    plugins,
  );

  const bulletsToRender = useMemo(() => {
    if (detectBulletCount && emblaApi) {
      return emblaApi.scrollSnapList().length;
    }
    return bulletCount;
  }, [bulletCount, emblaApi, detectBulletCount]);

  const scrollPrev = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
    if (autoplay) {
      autoplayRef.current.stop();
    }
  }, [autoplay, emblaApi, autoplayRef]);

  const scrollNext = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
    if (autoplay) {
      autoplayRef.current.stop();
    }
  }, [autoplay, emblaApi, autoplayRef]);

  const updateButtons = useCallback(() => {
    if (!emblaApi) return;
    setSnap(emblaApi.selectedScrollSnap());
    setEnableNext(emblaApi.canScrollNext());
    setEnablePrev(emblaApi.canScrollPrev);

    setCurrentSlide(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('settle', updateButtons);
    emblaApi.on('scroll', updateButtons);
    emblaApi.on('init', updateButtons);

    if (onPointer) {
      emblaApi.on('pointerDown', () => {
        if (onPointer) {
          onPointer(false);
        }
      });
      emblaApi.on('pointerUp', () => {
        if (onPointer) {
          onPointer(true);
        }
      });
    }
  }, [emblaApi, updateButtons, onPointer]);

  useEffect(() => {
    if (!onChange || !emblaApi) return;

    onChange({ currentSlide, slideCount: emblaApi.scrollSnapList().length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide, emblaApi]);

  useEffect(() => {
    if (customEmblaCarouselRef) {
      customEmblaCarouselRef.current = emblaApi;
    }
  }, [customEmblaCarouselRef, emblaApi]);

  return (
    <>
      <div
        {...props}
        className={['embla', 'custom-carousel', fillHeightClass, overflowClass].join(' ')}
      >
        <div className={['embla__viewport', fillHeightClass].join(' ')} ref={emblaRef}>
          <div className={['embla__container', fillHeightClass].join(' ')}>
            {children({ className: 'embla__slide', currentSlide })}
          </div>
        </div>
      </div>
      {enableBullets && (
        <section className="home-pagination">
          <button
            className={['embla__prev', enablePrevClass, bigArrowsClass, mobileArrowsClass].join(
              ' ',
            )}
            aria-label="Banner anterior"
            role="button"
            onClick={scrollPrev}
          />
          {!renderBullets && (
            <PaginationBullets
              value={bulletsToRender}
              id="homeCarouselPagination"
              carouselId="homeCarouselBanner"
              activeIndex={snap}
              onBulletClick={(index) => {
                emblaApi.scrollTo(index);
                if (autoplay) {
                  autoplayRef.current.reset();
                }
              }}
              ariaLabelBullet={ariaLabelBullet}
              bulletSize={8}
            />
          )}
          {!!renderBullets && renderBullets}
          <button
            className={['embla__next', enableNextClass, bigArrowsClass, mobileArrowsClass].join(
              ' ',
            )}
            aria-label="Próximo banner"
            role="button"
            onClick={scrollNext}
          />
        </section>
      )}
      <style jsx>{Styles}</style>
    </>
  );
}

CustomEmblaCarouselHome24.defaultProps = {
  children: () => null,
  fillHeight: true,
  loop: false,
  autoplay: false,
  onEmblaApiReady: () => {},
  onSlideChanged: () => {},
  hideOverflow: false,
  useBigArrows: false,
  enableBullets: false,
  options: {},
  enableMobileArrows: false,
  bulletCount: 5,
  ariaLabelBullet: [],
};

export default CustomEmblaCarouselHome24;
